import React, { useState, useRef, useEffect } from 'react';
import { Box, Slide } from '@mui/material';

interface BottomModalProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

const BottomModal: React.FC<BottomModalProps> = ({ open, children }) => {
  const [modalHeight, setModalHeight] = useState('40vh');
  const [isDragging, setIsDragging] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const startYRef = useRef(0);
  const startHeightRef = useRef(0);

  const handleTouchStart = (e: React.TouchEvent | React.MouseEvent) => {
    setIsDragging(true);
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    startYRef.current = clientY;
    startHeightRef.current = parseInt(modalHeight);
    document.body.style.userSelect = 'none'; // Evitar selección de texto durante el arrastre
  };

  const handleTouchMove = (e: TouchEvent | MouseEvent) => {
    if (!isDragging) return;
    
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const deltaY = startYRef.current - clientY;
    const newHeight = startHeightRef.current + (deltaY / window.innerHeight) * 100;
    
    // Limitar entre 20vh y 80vh
    const clampedHeight = Math.min(Math.max(newHeight, 20), 80);
    setModalHeight(`${clampedHeight}vh`);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    document.body.style.userSelect = '';
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleTouchMove);
      document.addEventListener('touchmove', handleTouchMove as EventListener);
      document.addEventListener('mouseup', handleTouchEnd);
      document.addEventListener('touchend', handleTouchEnd);
    } else {
      document.removeEventListener('mousemove', handleTouchMove);
      document.removeEventListener('touchmove', handleTouchMove as EventListener);
      document.removeEventListener('mouseup', handleTouchEnd);
      document.removeEventListener('touchend', handleTouchEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleTouchMove);
      document.removeEventListener('touchmove', handleTouchMove as EventListener);
      document.removeEventListener('mouseup', handleTouchEnd);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging]);

  return (
    <Slide direction="up" in={open} mountOnEnter unmountOnExit>
      <Box
        ref={modalRef}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: modalHeight,
          backgroundColor: 'white',
          zIndex: 1200,
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px',
          boxShadow: '0px -2px 10px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          touchAction: 'none', // Importante para evitar scroll no deseado en móviles
          transition: isDragging ? 'none' : 'height 0.2s ease-out' // Transición suave cuando no se está arrastrando
        }}
      >
        {/* Handle para arrastrar - ahora funcional */}
        <Box
          onTouchStart={handleTouchStart}
          onMouseDown={handleTouchStart}
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            padding: '8px 0',
            cursor: isDragging ? 'grabbing' : 'grab',
            touchAction: 'none'
          }}
        >
          <Box
            sx={{
              width: '40px',
              height: '4px',
              backgroundColor: isDragging ? '#aaa' : '#ddd',
              borderRadius: '2px',
              transition: 'background-color 0.2s'
            }}
          />
        </Box>
        {children}
      </Box>
    </Slide>
  );
};

export default BottomModal;